import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import { fetchApi } from '../services/api';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface BulkUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface UploadResult {
  총건수: number;
  신규등록: number;
  정보수정: number;
  메시지: string;
}

// 엑셀 데이터 타입 정의 추가
type ExcelRow = (string | number | null)[];

const COLUMN_OPTIONS = [
  { value: '고객명', label: '고객명' },
  { value: '성별', label: '성별' },
  { value: '영문성', label: '영문성' },
  { value: '영문이름', label: '영문이름' },
  { value: '이메일', label: '이메일' },
  { value: '대표전화', label: '연락처' },
  { value: '비고', label: '비고' },
];

const DEFAULT_MAPPING = ['', '', '', '', '', '', '']; // 모든 값을 빈 문자열로 초기화

const BulkUploadDialog: React.FC<BulkUploadDialogProps> = ({ open, onClose, onSuccess }) => {
  const [excelData, setExcelData] = useState<ExcelRow[]>([]);
  const [columnMapping, setColumnMapping] = useState<{ [key: number]: string }>({});
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const resetState = () => {
    setExcelData([]);
    setColumnMapping({});
    setLoading(false);
    setUploadResult(null);
    setConfirmDialogOpen(false);
    setPreviewData(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      const data = e.target?.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const parsedData = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as ExcelRow[];
      
      if (parsedData.length > 0) {
        // 헤더 행을 제외한 실제 데이터만 필터링
        const filteredData = parsedData.filter(row => {
          return !row.some(cell => 
            typeof cell === 'string' && (
              cell.includes('고객명') || 
              cell.includes('이메일')
            )
          );
        });

        if (filteredData.length > 0) {
          setExcelData(filteredData);
          // 초기 컬럼 매핑 설정
          const initialMapping: { [key: number]: string } = {};
          (filteredData[0] as ExcelRow).forEach((_: string | number | null, index: number) => {
            initialMapping[index] = DEFAULT_MAPPING[index] || '';
          });
          setColumnMapping(initialMapping);
        }
      }
    };

    reader.readAsBinaryString(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    }
  });

  const handleColumnMappingChange = (columnIndex: number, value: string) => {
    // 고객명이 선택된 경우, 다른 컬럼에서 고객명 선택을 해제
    if (value === '고객명') {
      const newMapping = { ...columnMapping };
      Object.keys(newMapping).forEach(key => {
        const numKey = parseInt(key);
        if (newMapping[numKey] === '고객명') {
          newMapping[numKey] = '';
        }
      });
      newMapping[columnIndex] = value;
      setColumnMapping(newMapping);
    } else {
      setColumnMapping(prev => ({
        ...prev,
        [columnIndex]: value
      }));
    }
  };

  const isUploadEnabled = () => {
    const mappedFields = Object.values(columnMapping);
    return (
      mappedFields.includes('고객명') &&
      mappedFields.includes('대표전화') &&
      // mappedFields.includes('이메일') &&
      excelData.length > 0 &&
      !loading
    );
  };

  const handleUpload = async () => {
    if (excelData.length === 0) {
      setSnackbar({
        open: true,
        message: '데이터가 없습니다.',
        severity: 'error'
      });
      return;
    }

    try {
      // 고객명 컬럼이 매핑되어 있는지 확인
      if (!Object.values(columnMapping).includes('고객명')) {
        setSnackbar({
          open: true,
          message: '고객명 컬럼을 필수로 매핑해주세요.',
          severity: 'error'
        });
        return;
      }

      // 첫 번째 실제 데이터를 미리보기로 설정
      const firstRowData = excelData[0];
      const previewRowData: Record<string, string> = {};
      
      // 컬럼 매핑을 사용하여 데이터 변환
      Object.entries(columnMapping).forEach(([index, field]) => {
        if (field) {
          previewRowData[field] = String(firstRowData[parseInt(index)] || '');
        }
      });

      setPreviewData({
        고객명: previewRowData['고객명'] || '',
        성별: previewRowData['성별'] || '',
        영문성: previewRowData['영문성'] || '',
        영문이름: previewRowData['영문이름'] || '',
        이메일: previewRowData['이메일'] || '',
        연락처: previewRowData['대표전화'] || '',
        총건수: excelData.length
      });

      setConfirmDialogOpen(true);
    } catch (error) {
      console.error('데이터 처리 중 오류:', error);
      setSnackbar({
        open: true,
        message: '데이터 처리 중 오류가 발생했습니다.',
        severity: 'error'
      });
    }
  };

  const handleConfirmUpload = async () => {
    setLoading(true);
    try {
      const transformedData = excelData.map(row => {
        const customerData: any = {
          고객명: '',
          성별: '',
          영문성: '',
          영문이름: '',
          대표전화: '',
          이메일: '',
          비고: ''
        };
        
        Object.entries(columnMapping).forEach(([index, field]) => {
          if (field) {
            customerData[field] = row[parseInt(index)] || '';
          }
        });
        
        return customerData;
      }).filter(data => 
        // 모든 값이 빈 문자열이 아닌 객체만 포함
        Object.values(data).some(value => value !== '')
      );

      const CompanyNo = localStorage.getItem('CompanyNo');
      const response = await fetchApi<{ table: UploadResult[] }>('ITN_T_고객일괄등록', [{
        컬럼명: '@업체번호',
        값: CompanyNo
      }, {
        컬럼명: '@고객데이터',
        값: JSON.stringify(transformedData)
      }]);

      if (response.data?.table?.[0]) {
        setUploadResult(response.data.table[0]);
        // 3초 후에 다이얼로그 닫기
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to upload customers:', error);
      setSnackbar({
        open: true,
        message: '고객 등록 중 오류가 발생했습니다.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }

    setConfirmDialogOpen(false);
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth={false}
        sx={{
          '& .MuiDialog-paper': {
            width: '1100px',
            height: '80vh',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1
          }}
        >
          <Typography variant="h6">고객 일괄 등록</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="text"
              startIcon={<CloseIcon />}
              onClick={handleClose}
              sx={{ color: 'white', height: '40px' }}
            >
              취소
            </Button>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleUpload}
              disabled={!isUploadEnabled()}
              sx={{ 
                bgcolor: 'white',
                height: '40px',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              {loading ? <CircularProgress size={24} /> : '업로드'}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {uploadResult && (
            <Alert 
              severity="success" 
              sx={{ 
                mt: 2, 
                mb: 2,
                '& .MuiAlert-message': {
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {uploadResult.메시지}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography>총 {uploadResult.총건수}건</Typography>
                <Typography>신규등록: {uploadResult.신규등록}건</Typography>
                <Typography>정보수정: {uploadResult.정보수정}건</Typography>
              </Box>
            </Alert>
          )}
          {excelData.length === 0 ? (
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                mt: 2,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <input {...getInputProps()} />
              <Typography>
                {isDragActive
                  ? "파일을 여기에 놓으세요..."
                  : "엑셀 파일을 드래그하여 놓거나 클릭하여 선택하세요"}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Typography 
                color="error" 
                sx={{ 
                  mb: 1, 
                  pl: 1,
                  fontSize: '0.875rem'
                }}
              >
                *고객명, 연락처 컬럼의 선택은 필수사항입니다.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {Array.from({ length: excelData[0].length }).map((_, index) => (
                      <TableCell key={index}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={columnMapping[index] || ''}
                            onChange={(e) => handleColumnMappingChange(index, e.target.value)}
                            sx={{ 
                              height: '36px',
                              '& .MuiSelect-select': {
                                py: 0.5
                              }
                            }}
                          >
                            <MenuItem value="">
                              <em>선택하세요</em>
                            </MenuItem>
                            {COLUMN_OPTIONS.map(option => (
                              <MenuItem 
                                key={option.value} 
                                value={option.value}
                                disabled={option.value === '고객명' && Object.values(columnMapping).includes('고객명') && columnMapping[index] !== '고객명'}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {excelData.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {(row as ExcelRow).map((cell, cellIndex) => (
                        <TableCell key={cellIndex}>{cell || ''}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          고객 일괄 등록 확인
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              첫 번째 데이터 확인
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 1 }}>
              <Typography>고객명:</Typography>
              <Typography>{previewData?.고객명}</Typography>
              <Typography>성별:</Typography>
              <Typography>{previewData?.성별}</Typography>
              <Typography>영문성:</Typography>
              <Typography>{previewData?.영문성}</Typography>
              <Typography>영문이름:</Typography>
              <Typography>{previewData?.영문이름}</Typography>
              <Typography>이메일:</Typography>
              <Typography>{previewData?.이메일}</Typography>
              <Typography>연락처:</Typography>
              <Typography>{previewData?.연락처}</Typography>
            </Box>
            <Typography sx={{ mt: 2, color: 'text.secondary' }}>
              외 {previewData?.총건수 - 1}명의 고객을 일괄 등록할까요?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>취소</Button>
          <Button onClick={handleConfirmUpload} variant="contained">
            등록
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadDialog; 